import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function TestimonialsPage() {
  const [selectedTestimonial, setSelectedTestimonial] = useState<number | null>(null);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<number | null>(null);
  const [filterCategory, setFilterCategory] = useState<'all' | 'agents' | 'developers' | 'designers'>('all');
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const extendedTestimonials = [
    {
      id: 1,
      name: 'Sarah Mitchell',
      role: 'Real Estate Agent',
      company: 'Coldwell Banker',
      location: 'Los Angeles, CA',
      image: 'https://public.readdy.ai/ai/img_res/4ccc5cc3139e39b6f3c4e9e3e3415259.jpg',
      rating: 5,
      category: 'agents',
      shortQuote: 'iVision transformed my listings completely. Properties that sat for months sold within weeks.',
      fullReview: 'When I first discovered iVision, I was skeptical about virtual staging. But after seeing the results on my first listing - a vacant condo that had been on the market for 4 months - I was completely converted. Within 2 weeks of adding the virtually staged photos, we had multiple offers. The buyers said they could finally envision themselves living there. Now I use iVision for every vacant property. The quality is indistinguishable from real staging at a fraction of the cost. My average days on market has decreased by 60%, and I\'ve closed 23 properties using their service in the past year alone.',
      results: ['73% faster sale time', '23 properties closed', '60% reduction in DOM'],
      yearsUsing: '2 years',
      propertiesSold: 23,
      videoUrl: null
    },
    {
      id: 2,
      name: 'Michael Chen',
      role: 'Property Developer',
      company: 'Horizon Properties',
      location: 'San Francisco, CA',
      image: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20an%20asian%20male%20business%20executive%20in%20his%2050s%20wearing%20navy%20suit%20confident%20expression%20neutral%20gray%20background%20studio%20lighting%20corporate%20portrait&width=200&height=200&seq=testimonial2&orientation=squarish',
      rating: 5,
      category: 'developers',
      shortQuote: 'We use iVision for all our pre-construction marketing. Pre-sales increased by 40%.',
      fullReview: 'In property development, pre-sales are everything. Before iVision, we relied on expensive model units and basic floor plans. Now we can show potential buyers exactly what their future home will look like with different furniture styles. Our pre-sales have increased by 40%, and the cost savings compared to physical staging are enormous. We\'ve used iVision on 8 different developments totaling over 400 units. The ability to show multiple design options for each floor plan has been game-changing. Buyers can choose their preferred style before the building is even finished, which has dramatically reduced our sales cycle time.',
      results: ['40% increase in pre-sales', '400+ units marketed', '$2.1M cost savings'],
      yearsUsing: '3 years',
      propertiesSold: 412,
      videoUrl: null
    },
    {
      id: 3,
      name: 'Jennifer Adams',
      role: 'Interior Designer',
      company: 'Adams Design Studio',
      location: 'Miami, FL',
      image: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20stylish%20female%20interior%20designer%20in%20her%2030s%20with%20creative%20look%20wearing%20modern%20outfit%20neutral%20background%20studio%20lighting%20artistic%20portrait&width=200&height=200&seq=testimonial3&orientation=squarish',
      rating: 5,
      category: 'designers',
      shortQuote: 'The attention to detail is remarkable. They perfectly captured my design vision.',
      fullReview: 'As an interior designer, I have very specific visions for my projects. What impressed me most about iVision is their ability to understand and execute my design concepts perfectly. I send them my mood boards and specifications, and they deliver exactly what I envisioned. My clients can now see the final result before we purchase a single piece of furniture. It has transformed how I present proposals. My client approval rate has gone from 65% to 90%, and I\'ve been able to take on 40% more projects because the visualization process is so much faster. The team is incredibly responsive and always willing to make adjustments until it\'s perfect.',
      results: ['90% client approval rate', '40% more projects', '100% satisfaction'],
      yearsUsing: '1.5 years',
      propertiesSold: 67,
      videoUrl: null
    },
    {
      id: 4,
      name: 'David Rodriguez',
      role: 'Luxury Real Estate Broker',
      company: 'Prestige Properties',
      location: 'Beverly Hills, CA',
      image: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20hispanic%20male%20luxury%20real%20estate%20broker%20in%20his%2040s%20wearing%20expensive%20suit%20confident%20smile%20neutral%20gray%20background%20studio%20lighting%20executive%20portrait&width=200&height=200&seq=testimonial4&orientation=squarish',
      rating: 5,
      category: 'agents',
      shortQuote: 'For luxury properties, presentation is everything. iVision delivers exceptional quality.',
      fullReview: 'In the luxury market, my clients expect perfection. When I first tried virtual staging, I was concerned about quality - would it look cheap or fake? iVision exceeded all expectations. The staging looks absolutely real, and the attention to detail in furniture selection and placement shows they understand high-end design. I\'ve used them on properties ranging from $2M to $15M, and every single client has been impressed. One $8.5M estate that had been sitting for 6 months sold within 3 weeks after we added virtually staged photos. The ROI is incredible - spending $500 on staging versus $15,000 for physical staging, with the same or better results.',
      results: ['$8.5M estate sold in 3 weeks', '97% client satisfaction', '$200K+ saved on staging'],
      yearsUsing: '2.5 years',
      propertiesSold: 34,
      videoUrl: null
    },
    {
      id: 5,
      name: 'Emily Thompson',
      role: 'Real Estate Team Leader',
      company: 'Thompson Realty Group',
      location: 'Austin, TX',
      image: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20confident%20female%20real%20estate%20team%20leader%20in%20her%2050s%20with%20professional%20smile%20wearing%20business%20blazer%20neutral%20gray%20background%20studio%20lighting%20leadership%20portrait&width=200&height=200&seq=testimonial5&orientation=squarish',
      rating: 5,
      category: 'agents',
      shortQuote: 'Our entire team uses iVision. It\'s become an essential part of our listing strategy.',
      fullReview: 'I lead a team of 12 agents, and we\'ve made iVision our standard for all vacant listings. The consistency and quality across all our listings has elevated our brand significantly. Sellers are impressed when we show them the staging options during listing presentations, and it\'s helped us win listings against competitors. The 24-hour turnaround time is incredible - we can have a property photographed on Monday and have staged photos ready for MLS by Tuesday. Our team has closed 156 transactions using iVision in the past 18 months, with an average of 45 days on market compared to the area average of 72 days. The cost savings alone have added over $180,000 to our bottom line.',
      results: ['156 transactions closed', '45 days avg DOM', '$180K+ saved'],
      yearsUsing: '1.5 years',
      propertiesSold: 156,
      videoUrl: null
    },
    {
      id: 6,
      name: 'Robert Kim',
      role: 'Investment Property Manager',
      company: 'Urban Investments LLC',
      location: 'Seattle, WA',
      image: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20an%20asian%20male%20investment%20property%20manager%20in%20his%2040s%20wearing%20business%20casual%20confident%20expression%20neutral%20gray%20background%20studio%20lighting%20corporate%20portrait&width=200&height=200&seq=testimonial6&orientation=squarish',
      rating: 5,
      category: 'developers',
      shortQuote: 'Managing 40+ rental properties, iVision helps us lease units 50% faster.',
      fullReview: 'We manage a portfolio of 40+ rental properties, and vacancy is our biggest cost. Since implementing iVision for all our vacant units, our average time to lease has dropped from 28 days to 14 days. That\'s a 50% reduction in vacancy costs. The staged photos get significantly more inquiries on rental listing sites, and prospective tenants can better visualize themselves in the space. We\'ve also seen a 15% increase in rental rates because the properties show so much better. For a property management company, these metrics directly impact our bottom line. We\'ve calculated that iVision has saved us over $120,000 in vacancy costs in just one year.',
      results: ['50% faster leasing', '15% higher rental rates', '$120K saved annually'],
      yearsUsing: '1 year',
      propertiesSold: 89,
      videoUrl: null
    },
    {
      id: 7,
      name: 'Lisa Martinez',
      role: 'Residential Architect',
      company: 'Martinez Architecture',
      location: 'Phoenix, AZ',
      image: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20hispanic%20female%20architect%20in%20her%2030s%20with%20creative%20professional%20look%20wearing%20modern%20outfit%20neutral%20gray%20background%20studio%20lighting%20artistic%20portrait&width=200&height=200&seq=testimonial7&orientation=squarish',
      rating: 5,
      category: 'designers',
      shortQuote: 'iVision helps my clients visualize architectural designs before construction begins.',
      fullReview: 'As an architect, I design spaces, but clients often struggle to visualize the final result from blueprints and renderings. iVision has become an invaluable tool in my design presentations. I can show clients how their future home will look fully furnished, which helps them understand spatial relationships and make better design decisions. This has reduced change orders during construction by about 30% because clients have a clearer vision from the start. I\'ve used iVision on 15 custom home projects, and it\'s helped me win several high-value contracts because my presentations are so much more compelling than competitors who only show empty renderings.',
      results: ['30% fewer change orders', '15 projects completed', '95% client satisfaction'],
      yearsUsing: '2 years',
      propertiesSold: 15,
      videoUrl: null
    },
    {
      id: 8,
      name: 'James Wilson',
      role: 'VP of Sales',
      company: 'Urban Development Co.',
      location: 'Austin, TX',
      image: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20caucasian%20male%20executive%20VP%20in%20his%2050s%20wearing%20expensive%20suit%20confident%20leadership%20expression%20neutral%20gray%20background%20studio%20lighting%20corporate%20portrait&width=200&height=200&seq=testimonial8&orientation=squarish',
      rating: 5,
      category: 'developers',
      shortQuote: 'The ability to show multiple design options for each unit was game-changing.',
      fullReview: 'We develop mid-rise condos and townhome communities, and pre-sales are critical to our financing. Before iVision, we would build one expensive model unit per floor plan, limiting buyers to a single design vision. Now we can show 3-4 different staging styles for each floor plan at a fraction of the cost. This has revolutionized our sales process. Buyers can choose contemporary, traditional, or transitional styles and see exactly what their unit will look like. Our pre-sales have increased by 40%, and we\'ve reduced our model unit costs by over $200,000 per project. We\'ve used iVision on 6 developments totaling 380 units, and the ROI is exceptional.',
      results: ['40% increase in pre-sales', '$200K saved per project', '380 units sold'],
      yearsUsing: '2 years',
      propertiesSold: 380,
      videoUrl: null
    },
    {
      id: 9,
      name: 'Amanda Foster',
      role: 'Home Stager & Designer',
      company: 'Foster Staging Co.',
      location: 'Nashville, TN',
      image: 'https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20blonde%20caucasian%20female%20home%20staging%20professional%20in%20her%20early%2040s%20with%20bright%20friendly%20smile%20wearing%20chic%20contemporary%20business%20outfit%20neutral%20beige%20background%20studio%20lighting%20warm%20inviting%20portrait&width=200&height=200&seq=testimonial9updated&orientation=squarish',
      rating: 5,
      category: 'designers',
      shortQuote: 'As a traditional stager, I was skeptical. Now virtual staging is 40% of my business.',
      fullReview: 'I\'ve been physically staging homes for 15 years, so I was initially resistant to virtual staging. But the market has changed, and clients are looking for cost-effective solutions. I partnered with iVision to offer virtual staging as an alternative for clients with budget constraints or properties in distant locations. I\'m amazed by the quality - it rivals my physical staging work. Now virtual staging represents 40% of my business revenue, and I can serve clients I would have had to turn away before. I still do physical staging for luxury properties, but for the mid-market, virtual staging is perfect. The combination has allowed me to grow my business by 60% in two years.',
      results: ['60% business growth', '40% revenue from virtual', '100+ projects completed'],
      yearsUsing: '2 years',
      propertiesSold: 127,
      videoUrl: null
    }
  ];

  const detailedCaseStudies = [
    {
      id: 1,
      title: 'Luxury Penthouse Transformation',
      client: 'Skyline Realty Group',
      location: 'Manhattan, NY',
      propertyType: 'Luxury Penthouse',
      propertySize: '3,200 sq ft',
      listingPrice: '$4.5M',
      challenge: 'A $4.5M penthouse sat vacant for 8 months with minimal interest. The empty 3,200 sq ft space felt cold and cavernous. Potential buyers struggled to visualize the potential, and showing requests were declining. The seller was considering a significant price reduction.',
      solution: 'iVision created a complete virtual staging package with modern luxury furnishings across all main living spaces. We staged the living room with a contemporary sectional and statement art, the dining area with a designer table for 8, the master suite with a luxurious bed and seating area, and converted one bedroom into a sophisticated home office. We used a contemporary minimalist style with warm wood accents and neutral tones to appeal to the target demographic.',
      process: [
        'Initial consultation to understand target buyer profile',
        'Professional photography of empty spaces',
        'Design concept approval with 3 style options',
        'Virtual staging of 6 rooms in 48 hours',
        'Delivery of high-resolution images for all marketing'
      ],
      results: [
        { metric: 'Days on Market', before: '240+', after: '21' },
        { metric: 'Showing Requests', before: '12 total', after: '47 in 3 weeks' },
        { metric: 'Final Sale Price', before: 'Listed at $4.5M', after: 'Sold at $4.65M' },
        { metric: 'Staging Cost', before: '$25K estimate', after: '$1,200' }
      ],
      beforeImage: 'https://readdy.ai/api/search-image?query=empty%20luxury%20penthouse%20living%20room%20with%20floor%20to%20ceiling%20windows%20manhattan%20skyline%20view%20hardwood%20floors%20white%20walls%20no%20furniture%20vacant%20space%20real%20estate%20photography&width=800&height=600&seq=case1before&orientation=landscape',
      afterImage: 'https://readdy.ai/api/search-image?query=luxuriously%20staged%20penthouse%20living%20room%20modern%20minimalist%20furniture%20cream%20sofa%20designer%20coffee%20table%20manhattan%20skyline%20view%20through%20floor%20to%20ceiling%20windows%20warm%20lighting%20elegant%20decor&width=800&height=600&seq=case1after&orientation=landscape',
      testimonial: 'The virtual staging completely changed how buyers perceived the space. We went from almost no interest to a bidding war. The property sold for $150K over asking, and the buyer specifically mentioned how the staged photos helped them envision their life there.',
      clientName: 'Robert Hayes',
      clientRole: 'Senior Broker',
      timeline: '3 weeks from staging to sale',
      roi: '12,150%'
    },
    {
      id: 2,
      title: 'Mid-Century Modern Revival',
      client: 'Heritage Homes Realty',
      location: 'Palm Springs, CA',
      propertyType: 'Single Family Home',
      propertySize: '2,400 sq ft',
      listingPrice: '$875K',
      challenge: 'A dated 1960s home needed to appeal to modern buyers while honoring its architectural heritage. The property had original terrazzo floors and iconic architecture, but felt empty and unloved. Traditional staging would cost $15,000+ and might not capture the mid-century aesthetic properly.',
      solution: 'We designed a mid-century modern staging concept that celebrated the home\'s original features while adding contemporary comfort. The staging included iconic Eames chairs, a Nelson bench, warm wood tones, and period-appropriate accessories. We highlighted the architectural details like exposed beams and the indoor-outdoor flow.',
      process: [
        'Research of authentic mid-century modern design',
        'Sourcing of period-appropriate furniture pieces',
        'Staging of living room, dining area, and master bedroom',
        'Emphasis on architectural features and natural light',
        'Coordination with photographer for optimal lighting'
      ],
      results: [
        { metric: 'Staging Cost', before: '$15,000 estimate', after: '$890' },
        { metric: 'Time to Offer', before: 'N/A', after: '9 days' },
        { metric: 'Over Asking', before: 'Listed at $875K', after: 'Sold at $920K' },
        { metric: 'Buyer Interest', before: 'Low', after: '23 showings' }
      ],
      beforeImage: 'https://readdy.ai/api/search-image?query=empty%20mid%20century%20modern%20home%20interior%20large%20windows%20open%20floor%20plan%20terrazzo%20floors%20dated%201960s%20architecture%20palm%20springs%20style%20vacant%20real%20estate%20photo&width=800&height=600&seq=case2before&orientation=landscape',
      afterImage: 'https://readdy.ai/api/search-image?query=beautifully%20staged%20mid%20century%20modern%20living%20room%20iconic%20furniture%20eames%20chairs%20nelson%20bench%20warm%20wood%20tones%20large%20windows%20palm%20springs%20style%20sophisticated%20retro%20design&width=800&height=600&seq=case2after&orientation=landscape',
      testimonial: 'iVision understood exactly what this home needed. The staging attracted buyers who appreciated the architecture and were willing to pay a premium. We received multiple offers, all above asking price.',
      clientName: 'Diana Martinez',
      clientRole: 'Listing Agent',
      timeline: '9 days to accepted offer',
      roi: '5,056%'
    },
    {
      id: 3,
      title: 'New Construction Condo Launch',
      client: 'Urban Development Co.',
      location: 'Austin, TX',
      propertyType: 'Condo Development (48 units)',
      propertySize: '650-1,400 sq ft units',
      listingPrice: '$285K-$625K',
      challenge: 'Marketing 48 units before construction completion. Needed to show 6 different floor plans in multiple style options to appeal to diverse buyers. Traditional model units would cost $180K+ and only show one design per floor plan.',
      solution: 'Created 18 unique virtual staging designs across 6 floor plans, each in 3 different styles: Modern, Transitional, and Industrial Chic. Developed a full marketing package including website integration, brochures, and interactive floor plan selector. Buyers could visualize their future home in their preferred style.',
      process: [
        'Analysis of target buyer demographics',
        'Development of 3 distinct design styles',
        'Staging of all 6 floor plans in each style',
        'Creation of interactive online gallery',
        'Integration with sales center presentation'
      ],
      results: [
        { metric: 'Pre-Sales', before: '0 units', after: '31 units (65%)' },
        { metric: 'Marketing Cost', before: '$180K traditional', after: '$12,500' },
        { metric: 'ROI', before: 'N/A', after: '1,340%' },
        { metric: 'Sales Cycle', before: 'N/A', after: '45 days avg' }
      ],
      beforeImage: 'https://readdy.ai/api/search-image?query=empty%20new%20construction%20condo%20unit%20concrete%20floors%20unfinished%20walls%20large%20windows%20city%20view%20modern%20architecture%20vacant%20space%20real%20estate%20developer%20photo&width=800&height=600&seq=case3before&orientation=landscape',
      afterImage: 'https://readdy.ai/api/search-image?query=stunning%20modern%20condo%20interior%20virtual%20staging%20contemporary%20furniture%20open%20concept%20living%20dining%20kitchen%20city%20views%20through%20large%20windows%20warm%20inviting%20atmosphere%20luxury%20finishes&width=800&height=600&seq=case3after&orientation=landscape',
      testimonial: 'The ability to show multiple design options for each unit was game-changing. Buyers could choose their style before the building was even finished. We sold 65% of units in pre-sales, which secured our construction financing and exceeded all projections.',
      clientName: 'James Wilson',
      clientRole: 'VP of Sales',
      timeline: '4 months to 65% pre-sales',
      roi: '1,340%'
    }
  ];

  const filteredTestimonials = filterCategory === 'all' 
    ? extendedTestimonials 
    : extendedTestimonials.filter(t => t.category === filterCategory);

  const handleViewPricing = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-[#F8F6F3] to-white">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-xs text-gray-500 mb-3 flex items-center justify-center gap-1.5">
              <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
              CLIENT SUCCESS STORIES
            </p>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Real Stories from
              <span className="block mt-1 text-[#FF6B6B]">Real Clients</span>
            </h1>
            <p className="text-base text-gray-600 leading-relaxed mb-6">
              Discover how real estate professionals, developers, and designers are transforming their businesses with iVision Virtual Staging. Read detailed reviews, watch video testimonials, and explore comprehensive case studies.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-9">
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="text-3xl font-bold text-[#FF6B6B] mb-1">500+</div>
                <p className="text-gray-600 text-xs">Happy Clients</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="text-3xl font-bold text-[#FF6B6B] mb-1">5,250+</div>
                <p className="text-gray-600 text-xs">Properties Staged</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="text-3xl font-bold text-[#FF6B6B] mb-1">4.9/5</div>
                <p className="text-gray-600 text-xs">Average Rating</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="text-3xl font-bold text-[#FF6B6B] mb-1">98%</div>
                <p className="text-gray-600 text-xs">Would Recommend</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-9 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-5">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => setFilterCategory('all')}
              className={`px-5 py-2 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                filterCategory === 'all'
                  ? 'bg-[#FF6B6B] text-white shadow-md'
                  : 'bg-[#F8F6F3] text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Reviews ({extendedTestimonials.length})
            </button>
            <button
              onClick={() => setFilterCategory('agents')}
              className={`px-5 py-2 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                filterCategory === 'agents'
                  ? 'bg-[#FF6B6B] text-white shadow-md'
                  : 'bg-[#F8F6F3] text-gray-700 hover:bg-gray-200'
              }`}
            >
              Real Estate Agents ({extendedTestimonials.filter(t => t.category === 'agents').length})
            </button>
            <button
              onClick={() => setFilterCategory('developers')}
              className={`px-5 py-2 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                filterCategory === 'developers'
                  ? 'bg-[#FF6B6B] text-white shadow-md'
                  : 'bg-[#F8F6F3] text-gray-700 hover:bg-gray-200'
              }`}
            >
              Developers ({extendedTestimonials.filter(t => t.category === 'developers').length})
            </button>
            <button
              onClick={() => setFilterCategory('designers')}
              className={`px-5 py-2 rounded-full text-xs font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                filterCategory === 'designers'
                  ? 'bg-[#FF6B6B] text-white shadow-md'
                  : 'bg-[#F8F6F3] text-gray-700 hover:bg-gray-200'
              }`}
            >
              Designers ({extendedTestimonials.filter(t => t.category === 'designers').length})
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-5">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTestimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-[#F8F6F3] rounded-xl p-6 hover:shadow-xl transition-all duration-300 group cursor-pointer"
                onClick={() => setSelectedTestimonial(testimonial.id)}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-md">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm">{testimonial.name}</h3>
                      <p className="text-gray-600 text-xs">{testimonial.role}</p>
                      <p className="text-gray-500 text-[10px]">{testimonial.company}</p>
                    </div>
                  </div>
                  {testimonial.videoUrl && (
                    <div className="w-8 h-8 flex items-center justify-center bg-[#FF6B6B] rounded-full">
                      <i className="ri-play-fill text-white text-sm"></i>
                    </div>
                  )}
                </div>

                {/* Rating */}
                <div className="flex items-center gap-0.5 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-[#FFB800] text-sm"></i>
                  ))}
                </div>

                {/* Quote */}
                <p className="text-gray-700 leading-relaxed mb-4 italic text-sm">
                  "{testimonial.shortQuote}"
                </p>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-[10px] text-gray-500 mb-0.5">Using iVision</p>
                    <p className="font-bold text-[#FF6B6B] text-xs">{testimonial.yearsUsing}</p>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <p className="text-[10px] text-gray-500 mb-0.5">Properties</p>
                    <p className="font-bold text-[#FF6B6B] text-xs">{testimonial.propertiesSold}</p>
                  </div>
                </div>

                {/* Location */}
                <p className="text-gray-500 text-xs flex items-center gap-1 mb-3">
                  <i className="ri-map-pin-line text-xs"></i>
                  {testimonial.location}
                </p>

                {/* Read More */}
                <button className="text-[#FF6B6B] font-medium text-xs flex items-center gap-1.5 group-hover:gap-2 transition-all duration-300 whitespace-nowrap">
                  Read Full Review
                  <i className="ri-arrow-right-line text-xs"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-14 bg-gradient-to-br from-[#F8F6F3] to-white">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-9">
            <p className="text-xs text-gray-500 mb-2 flex items-center justify-center gap-1.5">
              <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
              DETAILED CASE STUDIES
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              Real Results,
              <span className="block mt-1">Real Numbers</span>
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Explore in-depth case studies showing exactly how iVision helped clients achieve remarkable results
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {detailedCaseStudies.map((study) => (
              <div
                key={study.id}
                className="bg-white rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer"
                onClick={() => setSelectedCaseStudy(study.id)}
              >
                {/* Before/After Preview */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={study.afterImage}
                    alt={study.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-[10px] font-medium">
                    {study.propertyType}
                  </div>
                  <div className="absolute top-2 right-2 bg-[#FF6B6B] text-white px-2 py-1 rounded-full text-[10px] font-medium">
                    ROI: {study.roi}
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="text-base font-bold mb-1.5">{study.title}</h3>
                  <p className="text-gray-600 text-xs mb-3 flex items-center gap-1">
                    <i className="ri-map-pin-line text-xs"></i>
                    {study.location}
                  </p>

                  {/* Key Results Preview */}
                  <div className="space-y-1.5 mb-4">
                    {study.results.slice(0, 2).map((result, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">{result.metric}:</span>
                        <span className="font-bold text-[#FF6B6B]">{result.after}</span>
                      </div>
                    ))}
                  </div>

                  <button className="w-full py-2 bg-gray-900 text-white rounded-lg font-medium text-xs hover:bg-gray-800 transition-colors duration-300 whitespace-nowrap">
                    View Full Case Study
                    <i className="ri-arrow-right-line ml-1.5"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 bg-gray-900">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Join Our Success Stories?
          </h2>
          <p className="text-base text-gray-300 mb-6">
            Start transforming your listings today with professional virtual staging
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/#contact"
              className="px-6 py-3 bg-[#FF6B6B] text-white rounded-full font-medium text-sm hover:bg-[#ff5252] transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              Get Started Now
              <i className="ri-arrow-right-line ml-1.5"></i>
            </a>
            <button
              onClick={handleViewPricing}
              className="px-6 py-3 bg-white text-gray-900 rounded-full font-medium text-sm hover:bg-gray-100 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              View Pricing
            </button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-6 right-6 w-10 h-10 flex items-center justify-center bg-[#FF6B6B] text-white rounded-full shadow-lg hover:bg-[#ff5252] transition-all duration-300 cursor-pointer z-40 ${
          showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        aria-label="Back to top"
      >
        <i className="ri-arrow-up-line text-base"></i>
      </button>
    </div>
  );
}
